import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { ProductPodUtils } from '@thd-olt-component-react/product-pod';

export const INCREMENT_QUANTITY = 'product-list.increment-quantity';
export const REMOVE_PRODUCT = 'product-list.remove-product';
export const POST_LOAD_ERROR_ANALYTICS = 'product-list.post-load-error';
export const INTENT_SEARCH_EVENT = 'intent.search';
export const INTENT_EDIT_NAME_EVENT = 'intent.edit-name';
export const PRIVATE_LIST_ERROR = 'Private list cannot be shared by default access filter';
export const PRIVATE_LIST_COMPLETE_ERROR = `Exception while fetching data (/list) :
MyListException(mylistError=MyListError(code=null, message=Private list cannot be shared by default access filter,
  messageArgs=null, memberId=null, userId=null))`;
export const NO_LIST_EXIST_ERROR = 'No list exists';
export const NO_LIST_EXIST_COMPLETE_ERROR = `Exception while fetching data (/list) :
TransformerException(errorData=ErrorData(errors=[Error Details- Id: 'MYLIST_ERR_124', Description: 'null',
Submessage: 'No list exists with the given ListId or No items found for the given
ListId c5c55130-41d0-11ee-ba7d-7f76a49fc0db'],
httpStatus=404 NOT_FOUND, memberId=null, userId=null))`;

export const PAGE_SIZE = 6;

export const isIntentId = (id) => {
  // return false if itemId matches number pattern (assuming itemIds are numeric)
  const numberPattern = /^\d+$/;
  return !numberPattern.test(id);
};
export const getQuantity = ({ results, id }) => {
  if (!results || !Array.isArray(results)) {
    return undefined;
  }
  if (isIntentId(id)) {
    return results.find((listProduct) => listProduct.intentId === id)?.quantity;
  }
  return results.find((listProduct) => listProduct.itemId === id)?.quantity;
};

export const getFulfillmentLocation = ({ fulfillmentMethod, product, store }) => {
  const { getDeliveryZipCookie, isInStockNearby, getNearbyStoreInStock } = podFulFillmentUtils;
  switch (fulfillmentMethod) {
  case 'ShipToHome':
    return getDeliveryZipCookie() || store?.storeZip || 'DirectShip';
  case 'BOPIS':
    if (isInStockNearby(product)) return getNearbyStoreInStock(product)?.locationId || '';
    return store?.storeId || '';
  case 'ShipToStore':
    return store.storeId || '';
  case 'DirectDelivery':
  case 'DeliverFromStore':
    return getDeliveryZipCookie() || store.storeZip || '';
  default: return null;
  }
};

export const isGeneralMerchProduct = (product) => {
  const isGiftCard = product?.identifiers?.productType === 'GIFT_CARD';
  const isCustomProduct = product?.identifiers?.productType?.toUpperCase() === 'CONFIGURABLE_BLINDS';
  const isAppliance = product?.identifiers?.productType?.toUpperCase() === 'MAJOR_APPLIANCE';

  return !isCustomProduct && !isGiftCard && !isAppliance;
};

// Identifies if a product is a pure intent item (no catalog itemId)
// Pure intent: has intent/intentId but itemId is undefined/null/empty OR itemId equals intentId
export const isPureIntentItem = (item) => {
  if (!item) return false;

  const hasIntent = Boolean(item.intent || item.intentId);
  const hasItemId = Boolean(item.itemId);
  const itemIdEqualsIntentId = item.itemId === item.intentId;

  // Pure intent if:
  // - Has intent/intentId AND no itemId
  // - OR itemId equals intentId (backend sometimes sets itemId = intentId for pure intents)
  return hasIntent && (!hasItemId || itemIdEqualsIntentId);
};

// Gets the intent search term for replacing a product
// If the item has an original intent, use it; otherwise fall back to product label
export const getIntentForReplace = (listItem, product) => {
  // If item has original intent from starter list, use it
  if (listItem?.intent) {
    return listItem.intent;
  }
  // Otherwise use product description/label as the search term
  return product?.identifiers?.productLabel || '';
};

// Checks if a product belongs to a specific group
// Handles both regular groups and the special 'ungrouped' group
export const isProductInGroup = (product, groupId) => {
  return product.groupId === groupId || (groupId === 'ungrouped' && !product.groupId);
};

// Collects all catalog products from GroupAccordion selections
// Returns array of catalog product objects (not pure intents)
export const getSelectedCatalogProducts = ({ groupAccordionSelections, catalogProducts }) => {
  if (!groupAccordionSelections || !catalogProducts) {
    return [];
  }

  const { groupIds, itemIds } = groupAccordionSelections;
  const selectedCatalogItems = [];

  // Collect individually selected catalog items
  itemIds.forEach((id) => {
    const item = catalogProducts.find((product) => product.itemId === id || product.intentId === id);
    if (item) selectedCatalogItems.push(item);
  });

  // Collect catalog items from selected groups
  groupIds.forEach((groupId) => {
    const groupProducts = catalogProducts.filter((product) => isProductInGroup(product, groupId));
    selectedCatalogItems.push(...groupProducts);
  });

  return selectedCatalogItems;
};

// Collects all selected products from GroupAccordion selections (including intents)
// Returns array of all product objects for Add to List functionality
export const getSelectedProducts = ({ groupAccordionSelections, allProducts }) => {
  if (!groupAccordionSelections || !allProducts) {
    return [];
  }

  const { groupIds, itemIds } = groupAccordionSelections;
  const selectedItems = [];

  // Collect individually selected items (both catalog and intents)
  itemIds.forEach((id) => {
    const item = allProducts.find((product) => product.itemId === id || product.intentId === id);
    if (item) selectedItems.push(item);
  });

  // Collect all items from selected groups
  groupIds.forEach((groupId) => {
    const groupProducts = allProducts.filter((product) => isProductInGroup(product, groupId));
    selectedItems.push(...groupProducts);
  });

  return selectedItems;
};

/**
 * Calculates the lineItemType string in format "intentCount>productCount"
 * @param {Object} list - The list object with items or groups
 * @returns {string} - Format like "4>14" (4 intents, 14 products)
 */
export const calculateLineItemType = (list) => {
  let allItems = [];

  // Handle grouped lists (list.groups structure)
  if (list?.groups && Array.isArray(list.groups)) {
    allItems = list.groups.reduce((acc, group) => {
      if (group?.items && Array.isArray(group.items)) {
        return [...acc, ...group.items];
      }
      return acc;
    }, []);
  } else if (list?.items && Array.isArray(list.items)) {
    // Handle flat lists (list.items structure) - fallback
    allItems = list.items;
  }

  // If no items found, return 0>0
  if (allItems.length === 0) {
    return '0>0';
  }

  let intentCount = 0;
  let productCount = 0;

  allItems.forEach((item) => {
    if (isPureIntentItem(item)) {
      intentCount += 1;
    } else {
      productCount += 1;
    }
  });

  return `${intentCount}>${productCount}`;
};

export const getAtcEnabledProducts = ({ products, store }) => {
  const { getFulfillmentMethod } = podFulFillmentUtils;
  return (products || []).map((product) => {
    const fulfillmentMethod = isGeneralMerchProduct(product) ? null : getFulfillmentMethod(product);
    const fulfillmentLocation = isGeneralMerchProduct(product) ? null
      : getFulfillmentLocation({ fulfillmentMethod, product, store });
    return ProductPodUtils.isATCEnabled(product) ? {
      itemId: product.itemId,
      quantity: product.quantity,
      fulfillmentLocation,
      fulfillmentMethod
    } : null;
  }).filter((item) => item !== null);
};

export const debounce = (func, waitFor) => {
  let timeout;
  const executedFunction = (...args) => {
    const later = () => {
      timeout = clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, waitFor);
  };
  return executedFunction;
};

export const arrayUnique = (array) => {
  const newArray = [];
  array.forEach((item) => {
    if (!newArray.includes(item)) {
      newArray.push(item);
    }
  });
  return newArray;
};

export const unique = (value, index, array) => {
  return array.indexOf(value) === index;
};

export const triggerPostPageErrorAnalytics = ({ type, message, code, isSharedList }) => {
  LIFE_CYCLE_EVENT_BUS.trigger('product-list.post-load-error', {
    type,
    displayType: 'page',
    pageSection: isSharedList ? 'list details>shared' : 'list details',
    message,
    code
  });
};

export const getItemIdsMissingInPages = (itemIds, products, productInfoProducts, currentPage) => {
  if (!products || !Array.isArray(products)) {
    return [];
  }
  const productsAterDeletion = products.filter((item) => !itemIds.includes(item.itemId)) || [];
  const fetchedItemIds = productInfoProducts?.map((item) => item.itemId) || [];
  const newItemsOfAlreadyFetchedPages = productsAterDeletion.slice(0, (currentPage - 1) * PAGE_SIZE) || [];
  return newItemsOfAlreadyFetchedPages
    .filter((item) => !fetchedItemIds.includes(item.itemId))
    .map((item) => item.itemId);
};

export const addMissingField = ({ itemIds, searchData, listProducts }) => {
  return itemIds.map((id) => {
    const isIntent = isIntentId(id);
    const productData = searchData?.find((prod) => prod.itemId === id);
    const listProduct = listProducts?.find((listProd) => (
      listProd.itemId === id || (isIntent && listProd.intentId === id)
    ));
    const quantity = getQuantity({ results: listProducts, id });
    const groupId = listProduct?.groupId;
    const groupName = listProduct?.groupName;
    const intentId = listProduct?.intentId;
    if (isIntent) {
      return {
        itemId: id,
        intentId: id,
        missing: false,
        quantity,
        isDragged: false,
        groupId,
        groupName
      };
    }

    return productData ? {
      ...productData,
      intentId,
      missing: false,
      quantity,
      groupId,
      groupName
    } : {
      itemId: id,
      intentId,
      missing: true,
      quantity,
      isDragged: false,
      groupId,
      groupName
    };
  });
};

export const addToProductsDisplayed = ({
  productInfoProducts,
  productsDisplayed,
  isAddedMaterials
}) => {
  const newProducs = productsDisplayed ? [...productsDisplayed] : [];
  productInfoProducts.forEach((item) => {
    const index = newProducs?.findIndex((product) => product.itemId === item.itemId);
    if (isAddedMaterials && index === -1) {
      newProducs.splice(0, 0, item);
    } else if (!isAddedMaterials && index === -1) {
      newProducs.push(item);
    } else {
      newProducs.splice(index, 1, item);
    }
  });
  return newProducs;
};

export const isItemEqual = (prevProps, nextProps) => {
  return (
    prevProps.itemId === nextProps.itemId
    && prevProps.storeId === nextProps.storeId
    && prevProps.quantity === nextProps.quantity
    && prevProps.isDragging === nextProps.isDragging
  );
};

export const cleanIntent = (intent) => {
  const updatedIntent = (intent || '').trim().replace('~', '-');
  return encodeURIComponent(updatedIntent);
};

export const getProductsFromGroups = (list) => {
  if (!list?.groups) return [];

  return list.groups.reduce((acc, group) => {
    if (group?.items) {
      return [...acc, ...group.items];
    }
    return acc;
  }, []);
};

export const returnArray = (array) => array || [];
export const returnBoolean = (bool) => bool || false;
export const returnObject = (object) => object || {};
export const returnString = (string) => string || '';
export const createIntentGroups = async ({
  intentGroups = [],
  list,
  listOperations,
  refetch,
  onEditSuccess
}) => {
  // Validate required parameters
  if (!list?.id || !Array.isArray(intentGroups) || intentGroups.length === 0) {
    return;
  }

  // Calculate next group order based on existing groups
  const existingGroups = list.groups || [];
  const maxOrder = Math.max(
    0,
    ...existingGroups.map((group) => Number(group?.groupOrder) || 0).filter((val) => !Number.isNaN(val))
  );

  let nextGroupOrder = maxOrder + 1;

  // Separate groups into "create new" and "add to existing"
  const groupsToCreate = [];
  const groupsToAddTo = [];

  intentGroups.forEach((incomingGroup) => {
    const trimmedName = (incomingGroup?.groupName || '').trim();

    // Filter valid materials
    const validMaterials = (incomingGroup?.materials || []).filter(
      (material) => (material?.intent || '').trim() && material?.isSelected !== false
    );

    if (!validMaterials.length) return;

    // Map materials to products with intent
    const products = validMaterials.map((material, localIdx) => {
      return {
        intent: cleanIntent(material.intent),
        quantity: typeof material.quantity === 'number' && material.quantity > 0 ? material.quantity : 1,
        itemOrder: (material.itemOrder != null ? material.itemOrder : localIdx) + 1,
        itemId: material?.itemId || undefined
      };
    });

    // Check if groupId is null (create new group) or string (add to existing group)
    if (!incomingGroup?.groupId) {
      // Create new group
      if (!trimmedName) return; // Group name is required for creating new groups

      const payload = {
        groupName: trimmedName,
        groupOrder: String(nextGroupOrder),
        products
      };
      nextGroupOrder += 1;
      groupsToCreate.push(payload);
    } else if (typeof incomingGroup?.groupId === 'string') {
      // Add to existing group
      const targetGroup = existingGroups.find((group) => group.groupId === incomingGroup.groupId);
      const existingItemsCount = targetGroup?.items?.length || 0;

      // Update item orders to append to the end of existing items
      const adjustedProducts = products.map((product, index) => ({
        ...product,
        itemOrder: existingItemsCount + index + 1
      }));

      groupsToAddTo.push({
        groupId: incomingGroup.groupId,
        products: adjustedProducts
      });
    }
  });

  try {
    // Execute Create operation for new groups
    if (groupsToCreate.length > 0) {
      await listOperations({
        variables: {
          operationName: 'Create',
          id: list?.id,
          groups: groupsToCreate
        }
      });
    }

    // Execute AddItems operation for adding to existing groups
    if (groupsToAddTo.length > 0) {
      await listOperations({
        variables: {
          operationName: 'AddItems',
          id: list?.id,
          groups: groupsToAddTo
        }
      });
    }

    // Refetch to get updated data
    await refetch();
    onEditSuccess({});
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to create intent groups', err);
  }
};

export const groupToBeOpened = (groupedItems) => {
  if (!groupedItems.length) return null;

  // Since Default Group is always first, check if it has items
  const firstGroup = groupedItems[0]; // This is always the Default Group now
  if (firstGroup && firstGroup.noOfItems > 0) {
    return firstGroup?.id;
  }

  // If Default Group is empty, try second group (first named group)
  if (groupedItems.length > 1) {
    const nonEmptyGroup = groupedItems.find((groupedItem) => groupedItem?.noOfItems > 0);
    if (nonEmptyGroup) {
      return nonEmptyGroup?.id;
    }
  }

  return null;
};
